import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';


const PeriodSelector = ({ onPeriodChange }) => {
  const currentYear = new Date()?.getFullYear();
  const [selectedPeriodType, setSelectedPeriodType] = useState('yearly');
  const [selectedYear, setSelectedYear] = useState(currentYear?.toString());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Generate year options (last 10 years + current year + next year)
  const yearOptions = Array.from({ length: 12 }, (_, i) => {
    const year = currentYear - 10 + i;
    return {
      value: year?.toString(),
      label: year?.toString()
    };
  });

  const periodTypeOptions = [
    { value: 'yearly', label: 'Yearly' },
    { value: 'dateRange', label: 'Date Range' }
  ];

  const handlePeriodTypeChange = (value) => {
    setSelectedPeriodType(value);
    if (value === 'yearly') {
      onPeriodChange?.({ type: 'yearly', year: selectedYear });
    } else {
      onPeriodChange?.({ type: 'dateRange', startDate, endDate });
    }
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    onPeriodChange?.({ type: 'yearly', year: value });
  };

  const handleStartDateChange = (e) => {
    const value = e?.target?.value;
    setStartDate(value);
    if (value && endDate) {
      onPeriodChange?.({ type: 'dateRange', startDate: value, endDate });
    }
  };

  const handleEndDateChange = (e) => {
    const value = e?.target?.value;
    setEndDate(value);
    if (startDate && value) {
      onPeriodChange?.({ type: 'dateRange', startDate, endDate: value });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold text-foreground">Period Selection</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Period Type Selector */}
        <div className="md:col-span-3">
          <Select
            label="Period Type"
            options={periodTypeOptions}
            value={selectedPeriodType}
            onChange={handlePeriodTypeChange}
            placeholder="Select period type"
          />
        </div>

        {/* Conditional rendering based on period type */}
        {selectedPeriodType === 'yearly' ? (
          <div className="md:col-span-3">
            <Select
              label="Year"
              options={yearOptions}
              value={selectedYear}
              onChange={handleYearChange}
              placeholder="Select year"
              searchable
            />
          </div>
        ) : (
          <>
            <div className="md:col-span-4">
              <label className="text-sm font-medium leading-none text-foreground mb-2 block">
                Start Date
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                className="w-full"
                max={endDate || undefined}
              />
            </div>
            <div className="md:col-span-4">
              <label className="text-sm font-medium leading-none text-foreground mb-2 block">
                End Date
              </label>
              <Input
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                className="w-full"
                min={startDate || undefined}
              />
            </div>
          </>
        )}

        {/* Summary Display */}
        <div className="md:col-span-5 flex items-end">
          <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-2 w-full">
            <p className="text-xs text-blue-600 font-medium mb-1">Selected Period</p>
            <p className="text-sm text-blue-900 font-semibold">
              {selectedPeriodType === 'yearly' 
                ? `Year: ${selectedYear}`
                : startDate && endDate
                  ? `${new Date(startDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${new Date(endDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                  : 'Select start and end dates'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeriodSelector;